<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";

import { collectionGet } from "@/common/api/rest/collection";

import type { CollectionType } from "@/common/types/collection";

import {
  TabCollectionInfos,
  TabCollectionDetails,
  TabCollectionPlatform,
  TabCollectionGallery,
} from "@/components";
import { breadcrumbSimple, iconMenu, svgIcon } from "@/common/components";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const selectedTab = ref("infos");
const collectionData = ref<CollectionType | null>(null);

const labelTypes = computed(() => ({
  console: t(`collection.labels.consoles`),
  game: t(`collection.labels.games`),
  accessorie: t(`collection.labels.accessories`),
}));

const collectionId = computed(() => route.params.collection as string);
const type = computed(() => route.params.type as string);

const breadcrumbList = computed(() => [
  {
    key: "collections",
    label: t("common.labels.collections"),
    url: `/collection`,
  },
  {
    key: "collection",
    label: labelTypes.value[type.value],
    url: `/collection/${type.value}`,
  },
  {
    key: "edit",
    label: collectionData.value?.name ?? "...",
  },
]);

const menuList = computed(() => [
  {
    key: "infos",
    icon: "field",
    label: t("collection.labels.infos"),
    url: `/collection/${type.value}/${collectionId.value}/infos`,
  },
  {
    key: "gallery",
    icon: "no-image",
    label: t("collection.labels.images"),
    url: `/collection/${type.value}/${collectionId.value}/gallery`,
  },
  {
    key: "platform",
    icon: "platform",
    label: t("collection.labels.platforms"),
    url: `/collection/${type.value}/${collectionId.value}/platform`,
  },
  {
    key: "details",
    icon: "no-image",
    label: t("collection.labels.details"),
    url: `/collection/${type.value}/${collectionId.value}/details`,
  },
]);

function back() {
  router.push(`/collection/${type.value}`);
}

async function changeMenu(url: string) {
  await fetchData();
  router.push(url);
}

// collection
async function fetchData() {
  await collectionGet(collectionId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        collectionData.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watch(
  () => route.params.tab,
  () => {
    selectedTab.value = route.params.tab as string;
  },
  {
    immediate: true,
  }
);

onMounted(async () => {
  await fetchData();
});
</script>

<template>
  <article class="register-page">
    <section class="page-header">
      <breadcrumb-simple
        :items="breadcrumbList"
        :show-home="true"
        url-home="/home"
        active="edit"
      />
    </section>

    <section class="page-content">
      <div class="tab-header">
        <div class="title">
          <svg-icon icon="gamepad" size="2rem" />
          {{ t("collection.labels.edit_collection") }}
        </div>

        <div class="actions">
          <button class="btn btn-default w-8 h-8" @click="back()">
            <svg-icon icon="close" size="1rem" />
          </button>
        </div>
      </div>

      <div class="tab-content">
        <icon-menu
          :tabs="menuList"
          :active="selectedTab"
          size="3rem"
          @selected="changeMenu($event)"
        />

        <template v-if="collectionData">
          <template v-if="selectedTab === 'infos'">
            <tab-collection-infos
              :collection-id="collectionId"
              :collection="collectionData"
              @update:data="fetchData()"
            />
          </template>

          <template v-if="selectedTab === 'details'">
            <tab-collection-details :collection="collectionData" />
          </template>

          <template v-if="selectedTab === 'platform'">
            <tab-collection-platform :collection="collectionData" />
          </template>

          <template v-if="selectedTab === 'gallery'">
            <tab-collection-gallery :collection="collectionData" />
          </template>
        </template>
      </div>
    </section>
  </article>
</template>

<style lang="scss" scoped>
.register-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;

    .tab-header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding-inline: 1.25rem;
      padding-block: 0.75rem;
      margin-block-end: 1.5rem;
      border-block-end: 1px solid var(--border);

      .title {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.2rem;
        text-transform: uppercase;
      }

      .actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        gap: 0.75rem;

        button {
          width: 1.75rem;
          height: 1.75rem;
          padding: 0;
        }
      }
    }

    .tab-content {
      display: flex;
      flex-direction: column;
      gap: 1.54rem;
      padding-inline: 1.25rem;
      padding-block-end: 1.25rem;
    }
  }
}
</style>
